import MainLoader from '@/components/Loader';
import useIntl from '@/infrastructure/hooks/useIntl';
import { widgetCurrencyAtom } from '@/infrastructure/state/WidgetCurrency/widgetCurrency.ts';
import { customerInfoAtom } from '@/infrastructure/state/customerInfo';
import useCreateInitialRequest from '@/infrastructure/state/useCreateInitialRequest.ts';
import { checkIfStandalone } from '@/utils/checkIfIFrame.ts';
import {
  ActionIcon,
  Divider,
  Group,
  Paper,
  Stack,
  Textarea
} from '@mantine/core';
import { useAtom } from 'jotai';
import {
  ChangeEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';

import Send from '@assets/send.svg';

import { useFocus } from './ChatInputContext.tsx';
import useTrackEvent from '@/infrastructure/analytics/useTrackEvent';

import Footer from './components/Footer';
import ChatMenu from './components/Menu';
import Message from './components/Message';
import styles from './index.module.css';
import { IMessage } from './state';
import useChat from './useChat';
import useChatInit from './useChatInit';
import useHandleChatRequest, { RequestType } from './useHandleChatRequest.ts';

import { chatRequestsAtom } from '@/infrastructure/state/chatRequests';
import { AccommodationsSearchRequestDto } from '../AccommodationList/api/requestTypes';

const Chat = () => {
  const { trackEvent } = useTrackEvent();
  const t = useIntl('chat');
  const virtuoso = useRef<VirtuosoHandle>(null);
  const { isChatLoaded, init, restartChat } = useChatInit();
  const { messages, sendMessage } = useChat();
  const [newMessage, setNewMessage] = useState<string>('');
  const [customerInfoState] = useAtom(customerInfoAtom);
  const [widgetCurrencyState] = useAtom(widgetCurrencyAtom);
  const { inputRef } = useFocus();
  const shouldAutofocusChat = useMemo(checkIfStandalone, []);
  const { handleChatRequest } = useHandleChatRequest();
  const [chatRequests] = useAtom(chatRequestsAtom);

  const latestRequest = useMemo(() => {
    const values = Object.values(chatRequests);
    for (let i = values.length - 1; i >= 0; i--) {
      const req = values[i];
      if (req && 'adults' in req) {
        return req as AccommodationsSearchRequestDto;
      }
    }
    return undefined;
  }, [chatRequests]);

  const guestsCount = latestRequest ? (latestRequest.adults + (latestRequest.children || 0)) : undefined;
  const roomsCount = latestRequest ? latestRequest.rooms : undefined;

  const { initialRequest } = useCreateInitialRequest();
  
  useEffect(() => {
    if (customerInfoState.isInitialized) {
      init(initialRequest).catch(ex => {
        //
      });
    }
  }, [customerInfoState.isInitialized]);

  useEffect(() => {
    if (initialRequest && widgetCurrencyState.widgetCurrency) {
      handleChatRequest({
        request_type: RequestType.accommodation,
        preferences: initialRequest
      });
    }
  }, [initialRequest, widgetCurrencyState.widgetCurrency]);

  const onChangeMessage = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const message = event.target.value;

    setNewMessage(message);
  };

  const onKeyDownMessage = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key == 'Enter') {
      event.preventDefault();
      submitMessage();
    }
  };

  const submitMessage = () => {
    if (newMessage?.trim()) {
      trackEvent('ask_in_chat_clicked', {
        category: 'Widget',
        surface: 'chat',
        map_bounds_present: 'no',
        guests_count: guestsCount,
        rooms_count: roomsCount
      });
      sendMessage(newMessage);
    }

    inputRef.current?.focus();
    setNewMessage('');
  };

  return (
    <Paper className={styles.container} withBorder>
      <div className={styles.header}>
        <ChatMenu resetChat={restartChat} />
      </div>
      <Divider />
      <Stack gap="sm" mx="sm" className={styles.messageContainer}>
        {isChatLoaded ? (
          <Virtuoso
            ref={virtuoso}
            className={styles.list}
            style={{ height: '100%', margin: '0' }}
            data={messages}
            increaseViewportBy={200}
            followOutput={isAtBottom => true}
            itemContent={(index: number, message: IMessage) => (
              <Message message={message} />
            )}
            components={{ Footer }}
          />
        ) : (
          <MainLoader />
        )}
      </Stack>
      <Group className={styles.inputGroup} gap="sm" align="flex-start">
        <Textarea
          autosize
          ref={inputRef}
          id="chatInput"
          radius="md"
          placeholder={t('typeYourMessagePlaceholder')}
          classNames={{
            root: styles.inputRoot
          }}
          autoFocus={shouldAutofocusChat}
          value={newMessage}
          onChange={onChangeMessage}
          onKeyDown={onKeyDownMessage}
        />
        <ActionIcon
          variant="primary"
          size="lg"
          radius="md"
          onClick={submitMessage}
        >
          <Send />
        </ActionIcon>
      </Group>
    </Paper>
  );
};

export default Chat;
